import React, { useMemo, useState } from "react";
import { Home } from "lucide-react";
import { Color } from "../../styles/Colors";
import { formatAddress } from "../../utils";
import {
  IconWrapper,
  NoData,
  StyledTable,
  TableContainer,
  Td,
  Th,
  Tr,
} from "./ProjectListTable.styles";
import ProjectTypeIcon from "../Icons/IconHome";

interface Project {
  city?: string;
  pType?: string;
  county?: string;
  tlSqFoot?: string;
  pName?: string;
  st?: string;
  addr?: string;
  pId?: string;
  zip?: string;
  compDate?: string;
  fundedDate?: string;
  matDate?: string;
  pm?: string;
  pLoanId?: string;
  maxStories?: number;
}

interface ProjectListTableProps {
  projects: Project[];
  onRowClick?: (projectId: string) => void;
}

const ProjectListTable: React.FC<ProjectListTableProps> = ({
  projects,
  onRowClick,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig?.key === key && sortConfig.direction === "asc")
      direction = "desc";
    else if (sortConfig?.key === key && sortConfig.direction === "desc")
      direction = "asc";
    setSortConfig({ key, direction });
  };

  const sortedProjects = useMemo(() => {
    if (!sortConfig) return projects;
    return [...projects].sort((a: any, b: any) => {
      const aVal = (a[sortConfig.key] ?? "").toString().toLowerCase();
      const bVal = (b[sortConfig.key] ?? "").toString().toLowerCase();
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [projects, sortConfig]);

  const renderSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <svg width="12" height="12" fill="#ccc" viewBox="0 0 24 24">
            <path d="M12 4 L6 10 H18 Z" /> {/* Up arrow (gray) */}
            <path d="M12 20 L6 14 H18 Z" /> {/* Down arrow (gray) */}
          </svg>
        </span>
      );
    }
    if (sortConfig.direction === "asc") {
      return (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <svg width="12" height="12" fill="#0f62e7" viewBox="0 0 24 24">
            <path d="M7 14l5-5 5 5H7z" /> {/* Up arrow */}
          </svg>
        </span>
      );
    }
    if (sortConfig.direction === "desc") {
      return (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            alignItems: "center",
          }}
        >
          <svg width="12" height="12" fill="#0f62e7" viewBox="0 0 24 24">
            <path d="M7 10l5 5 5-5H7z" /> {/* Down arrow */}
          </svg>
        </span>
      );
    }
  };

  const columns = [
    { key: "pName", label: "Project Name" },
    { key: "pLoanId", label: "ProjectID" },
    { key: "pType", label: "Type" },
    { key: "addr", label: "Address" },
    { key: "county", label: "County" },
    { key: "tlSqFoot", label: "Sq. Ft" },
    { key: "maxStories", label: "Stories" },
    { key: "fundedDate", label: "Funded Date" },
    { key: "matDate", label: "Maturity Date" },
    // { key: "pm", label: "Project Manager" },
  ];

  return (
    <TableContainer>
      {sortedProjects.length === 0 ? (
        <NoData>No projects found</NoData>
      ) : (
        <StyledTable>
          <thead>
            <tr>
              {columns.map(({ key, label }) => (
                <Th
                  key={key}
                  onClick={() => handleSort(key)}
                  $active={sortConfig?.key === key}
                >
                  {label}
                  {renderSortIcon(key)}
                </Th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedProjects.map((item, index) => {
              const {
                city,
                pType,
                county,
                tlSqFoot,
                maxStories,
                pName: projectName,
                st: stateCode,
                addr: stAddress,
                pId: projectId,
                zip,
                fundedDate,
                matDate,
                // pm,
                pLoanId: projectLoanIDNumber,
              } = item || {};

              const address = formatAddress(
                {
                  stAddress: stAddress || "",
                  city: city || "",
                  stateCode: stateCode ? stateCode.toUpperCase() : "",
                  zip: zip || "",
                },
                true,
              );

              return (
                <Tr
                  key={index}
                  onClick={() => projectId && onRowClick?.(projectId)}
                >
                  <Td>
                    <IconWrapper>
                      {<ProjectTypeIcon pType={item?.pType} size={40} />}
                      <span>{projectName || "-"}</span>
                    </IconWrapper>
                  </Td>
                  <Td>{projectLoanIDNumber || "-"}</Td>
                  <Td>{pType || "-"}</Td>
                  <Td>{address || "-"}</Td>
                  <Td>{county || "-"}</Td>
                  <Td>{tlSqFoot || "-"}</Td>
                  <Td>{maxStories || "-"}</Td>
                  <Td>{fundedDate || "-"}</Td>
                  <Td>{matDate || "-"}</Td>
                  {/* <Td>{pm || "-"}</Td> */}
                </Tr>
              );
            })}
          </tbody>
        </StyledTable>
      )}
    </TableContainer>
  );
};

export default ProjectListTable;
